"use client";

import React, { ReactNode } from "react";

import { PAGE_HEADER, TYPOGRAPHY } from "@/lib/constants/layoutConstants";

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  className?: string;
  id?: string;
}

/**
 * Standardized page header component with consistent typography and spacing
 * Uses org colors via CSS variables (already set up in theme)
 */
export default function PageHeader({
  actions,
  className = "",
  description,
  id,
  title,
}: PageHeaderProps) {
  // Generate unique ID for description for ARIA association
  const descriptionId = description ? `page-description` : undefined;

  return (
    <header
      className={`flex flex-col sm:flex-row justify-between items-start sm:items-center ${PAGE_HEADER.MARGIN_BOTTOM} gap-4 w-full ${className}`}
      data-testid="page-header"
    >
      <div>
        <h1
          className={TYPOGRAPHY.PAGE_TITLE}
          {...(descriptionId && { "aria-describedby": descriptionId })}
          id={id}
        >
          {title}
        </h1>
        {description && (
          <p
            className={`mt-1 ${TYPOGRAPHY.DESCRIPTION}`}
            data-testid="page-header-description"
            id={descriptionId}
          >
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div aria-label="Page actions" className="flex gap-2">
          {actions}
        </div>
      )}
    </header>
  );
}
